import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { VocabularyEntry, VocabularyQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'

/** Das Recht, das diese Ansicht braucht. */
const PERMISSION = 'vocabulary:read'

//: Knapp 300 Zeilen. Eine Seite von 100 zeigt jeden Raum außer den Ländern
//: vollständig; wer die Länder durchsieht, blättert oder sucht.
const PAGE_SIZE = 100

/** Der Raum in Worten.
 *
 * Die Zuordnung steht HIER und nicht im Backend: `geo` ist der Speicherwert,
 * "Geografie" ist Deutsch und damit Sache der Anzeige — dieselbe Überlegung
 * wie bei den Rohstoffkategorien.
 *
 * Was hier fehlt, wird zum Rohwert selbst. Das ist Absicht: Kommt ein Raum
 * über eine Ladedatei dazu, erscheint er sichtbar statt verschwiegen.
 */
const RAEUME: Record<string, string> = {
  geo: 'Geografie',
  crypt: 'Krypto',
  mkt: 'Markt',
}

/** Wofür eine Norm steht — als Hinweis in der Auswahl. */
const NORM_HINWEIS: Record<string, string> = {
  ISO3166: 'Länder, dazu EU, EZ und UN',
  M49: 'Regionen und Kontinente',
  IW: 'unsere eigene Setzung',
  SYM: 'gebräuchliches Kürzel',
}

//: Auswahl und Adresse: In der Adresse steht eine kommagetrennte Liste
//: (?namespace=geo,mkt), die Komponente arbeitet mit einem Array.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

const COLUMNS: Column<VocabularyEntry>[] = [
  { key: 'name', label: 'Name', sortable: true, minWidth: '220px' },
  // Der Verweis ist kopierbar wie die ISIN am Instrument: Er ist der Wert,
  // den man von hier aus weitergibt — in einen Filter, ein Ticket, eine
  // Meldung. Abgeschrieben aus drei Spalten wäre er eine Fehlerquelle.
  { key: 'ref', label: 'Verweis', mono: true, copy: true, minWidth: '190px',
    help: 'So steht die Größe in den Verweisen einer Meldung, eines Ereignisses oder einer Einschätzung: Raum, Norm und Code mit Doppelpunkten. Der Raum sagt, worum es sich handelt; die Norm, woher der Code stammt.' },
  { key: 'namespace', label: 'Raum', sortable: true, minWidth: '120px',
    help: 'Was für eine Größe es ist. Zugleich das Präfix des Verweises.',
    render: (row) => RAEUME[row.namespace] ?? row.namespace },
  { key: 'scheme', label: 'Norm', sortable: true, mono: true, minWidth: '110px',
    help: 'Woher der Code stammt — NICHT die Art des Dings. Deshalb steht die Europäische Union unter ISO3166, weil die Norm sie neben den Ländern führt, und die NATO unter IW, weil keine Norm sie führt. Wo ISO 3166 einen Code hat, gilt ISO 3166; sonst stünde dieselbe Größe zweimal da.' },
  { key: 'code', label: 'Code', sortable: true, mono: true, minWidth: '110px',
    help: 'Der Code innerhalb seiner Norm. Nur zusammen mit Raum und Norm eindeutig: 10 ist bei GICS die Energie und bei IW die Halbleiter, 002 bei M49 Afrika.' },
  // Nicht sortierbar: Die Spalte ist bei fünf von sechs Zeilen leer, und eine
  // Sortierung danach ordnete vor allem Lücken.
  { key: 'parent_code', label: 'Übergeordnet', mono: true, minWidth: '130px',
    help: 'Die geografische Schachtelung von UN M49 — Westeuropa unter Europa unter Welt. Nur dort gefüllt: Bündnisse bleiben leer, weil ein Land gleichzeitig zu EU, NATO, G7 und G20 gehört und ein Elternfeld nur eines tragen kann. Die Zuordnung Land zu Region steht noch aus.',
    render: (row) => row.parent_code ?? '—' },
]

export function VocabularyPage() {
  const allowed = useHasPermission(PERMISSION)

  // Die vorhandenen Raum-Norm-Paare kommen vom Server und nicht aus einer
  // Konstanten: Kommt ein Raum über eine Ladedatei dazu, steht er ohne
  // Codeänderung im Filter. Genau dafür liegt das Vokabular in der Datenbank.
  const [raeume, setRaeume] = useState<MultiSelectOption[]>([])
  const [normen, setNormen] = useState<MultiSelectOption[]>([])

  useEffect(() => {
    if (!allowed) return
    const abbruch = new AbortController()
    api.vocabularyNamespaces(abbruch.signal).then((paare) => {
      setRaeume([...new Map(paare.map((p) => [p.namespace, {
        value: p.namespace,
        label: RAEUME[p.namespace] ?? p.namespace,
        hint: p.namespace,
      }])).values()])
      setNormen([...new Map(paare.map((p) => [p.scheme, {
        value: p.scheme,
        label: p.scheme,
        hint: NORM_HINWEIS[p.scheme],
      }])).values()])
    }).catch(() => {
      // Ohne die Paare bleiben die Auswahlfelder leer — die Liste und die
      // Suche darüber funktionieren weiter. Ein Fehlerkasten für einen
      // ausgefallenen Filter wäre lauter als der Schaden.
    })
    return () => abbruch.abort()
  }, [allowed])

  // Der Zustand steht in der Adresse, nicht in der Komponente: teilbar,
  // überlebt das Neuladen, und die Zurück-Taste nimmt den letzten Schritt
  // zurück statt die Seite zu verlassen.
  const list = useListParams({
    filters: { q: '', namespace: '', scheme: '' },
    sort: 'name',
  })

  const params: VocabularyQuery = {
    q: list.filters.q.trim() || undefined,
    namespace: alsListe(list.filters.namespace),
    scheme: alsListe(list.filters.scheme),
    sort: list.sort as VocabularyQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
    total: true,
  }

  // Nicht abfragen, wenn das Recht ohnehin fehlt: Der Aufruf liefe garantiert
  // ins 403.
  const { result, loading, error, denied } = useListQuery(
    api.vocabulary, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="das Vokabular" />
  }

  return (
    <>
      <FilterBar>
        <FilterGrow>
          {/*
            Der Hinweis steht im Platzhalter und nicht als eigene Zeile unter
            dem Feld — sonst stünde es höher als die Auswahlfelder daneben.
          */}
          <Input label="Suche"
                 placeholder="Name oder Code — „Deutschland“, „DE“, „150“"
                 value={list.filters.q} autoFocus
                 onChange={(event) =>
                   list.setFilter('q', event.target.value)} />
        </FilterGrow>
        {/*
          Zwei Mehrfachauswahlen. Nichts gewählt heißt "alle" — nicht "keiner
          von diesen".
        */}
        <FilterFixed>
          <MultiSelect label="Raum" options={raeume}
                       selected={alsListe(list.filters.namespace)}
                       onChange={(werte) =>
                         list.setFilter('namespace', werte.join(','))}
                       placeholder="alle Räume"
                       searchPlaceholder="Geografie, Markt …" />
        </FilterFixed>
        <FilterFixed>
          <MultiSelect label="Norm" options={normen}
                       selected={alsListe(list.filters.scheme)}
                       onChange={(werte) =>
                         list.setFilter('scheme', werte.join(','))}
                       placeholder="alle Normen"
                       searchPlaceholder="ISO3166, M49 …" />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        {/*
          Ohne `onRowClick`: Es gibt keine Detailansicht, weil es nichts zu
          zeigen gäbe — alle fachlichen Felder stehen schon hier.
        */}
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          loading={loading && result === null}
          empty={<EmptyState title="kein Treffer">
            Kein Eintrag passt zu Suche und Filter. Das Vokabular wird beim
            Ladelauf gefüllt und hier nicht bearbeitet.
          </EmptyState>}
        />
      </Card>
    </>
  )
}
